/**
 * Admin API — data import/export domain.
 *
 * CSV/Excel import + export for location services and account locations,
 * plus copy-structure between locations. Extracted from the AdminService
 * god class in services/auth.ts and converted to the central web API
 * client (except the two export methods, which must read the
 * Content-Disposition response header — see TODOs).
 */

import { webApiClient, unwrap } from '../apiClient';
import type { ImportResponse, LocationImportResponse } from '../auth';

// Import departments and services from CSV/XLS file.
// Pass confirmRestoreAll=true to acknowledge any restore-required conflicts surfaced
// by a prior call (status='needs_resolution').
export async function importLocationData(locationId: string, file: File, confirmRestoreAll: boolean = false): Promise<ImportResponse> {
  const form = new FormData();
  form.append('file', file);
  if (confirmRestoreAll) {
    form.append('confirm_restore_all', 'true');
  }

  const json = await unwrap(
    webApiClient.postFormData<ImportResponse>(`/locations/${locationId}/services/import`, form),
    'Failed to import data'
  );
  return json as ImportResponse;
}

// Export departments and services to CSV or Excel file
export async function exportLocationServices(locationId: string, format: 'csv' | 'excel'): Promise<{ blob: Blob; filename: string }> {
  const params = new URLSearchParams();
  params.set('format', format);

  // TODO(webApiClient): needs response headers — the server-provided
  // filename is parsed from Content-Disposition, which the central client
  // does not expose. Keep as raw fetch until the client surfaces headers.
  const url = `${webApiClient.getBaseUrl()}/locations/${locationId}/services/export?${params.toString()}`;
  // eslint-disable-next-line no-restricted-globals -- needs the Content-Disposition response header for the filename; see TODO(webApiClient) above
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
  });

  if (!res.ok) {
    let message = 'Failed to export data';
    try {
      const body = await res.text();
      if (body) {
        try {
          const j = JSON.parse(body);
          message = j.error || j.message || body;
        } catch {
          message = body;
        }
      }
    } catch {}
    throw new Error(message);
  }

  // Extract filename from Content-Disposition header
  const contentDisposition = res.headers.get('Content-Disposition');
  let filename = `services_export_${new Date().toISOString().split('T')[0]}.${format === 'csv' ? 'csv' : 'xlsx'}`;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1];
    }
  }

  const blob = await res.blob();
  return { blob, filename };
}

// Export every location for an account (with the full accounting block) as
// CSV. Useful for bulk-editing accounting fields (e.g. backfilling
// accounting_default_location for all dealers under a NetSuite-connected
// account) and importing the result back via importLocations.
export async function exportLocations(accountId: string, format: 'csv' | 'xlsx' = 'csv'): Promise<{ blob: Blob; filename: string }> {
  // TODO(webApiClient): needs response headers — filename comes from
  // Content-Disposition, which the central client does not expose.
  const url = `${webApiClient.getBaseUrl()}/accounts/${accountId}/locations/export?format=${encodeURIComponent(format)}`;
  // eslint-disable-next-line no-restricted-globals -- needs the Content-Disposition response header for the filename; see TODO(webApiClient) above
  const res = await fetch(url, { method: 'GET', credentials: 'include' });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Failed to export locations: ${body || res.statusText}`);
  }
  const contentDisposition = res.headers.get('Content-Disposition') ?? '';
  const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
  const filename = filenameMatch?.[1] ?? `locations_${new Date().toISOString().split('T')[0]}.${format}`;
  return { blob: await res.blob(), filename };
}

// Bulk-create or update locations for an account from a CSV / XLSX file.
// Match key is Location ID when present, then Name (case-insensitive).
// Returns row-by-row results so the UI can surface created / updated /
// skipped / error counts plus per-row error messages.
export async function importLocations(accountId: string, file: File): Promise<LocationImportResponse> {
  const form = new FormData();
  form.append('file', file);
  const json = await unwrap(
    webApiClient.postFormData<LocationImportResponse>(`/accounts/${accountId}/locations/import`, form),
    'Failed to import locations'
  );
  return json as LocationImportResponse;
}

// Copy department structure from another location
export async function copyLocationStructure(
  targetLocationId: string,
  sourceLocationId: string
): Promise<{
  success: boolean;
  summary: {
    department_sections: {
      copied: number;
      skipped: number;
      total: number;
    };
    departments: {
      copied: number;
      skipped: number;
      total: number;
    };
    services: {
      copied: number;
      skipped: number;
      total: number;
    };
    service_relationships: {
      copied: number;
      skipped: number;
      total: number;
    };
  };
  skipped_details?: Array<{
    type: string;
    name: string;
    reason: string;
  }>;
  message?: string;
}> {
  const result = await unwrap(
    webApiClient.post<any>(`/locations/${targetLocationId}/copy-structure`, { source_location_id: sourceLocationId }),
    'Failed to copy structure'
  );
  return result;
}

export const importExportAdmin = {
  importLocationData,
  exportLocationServices,
  exportLocations,
  importLocations,
  copyLocationStructure,
};
